import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useGalleries } from '../contexts/GalleryContext';

// 플레이스홀더 이미지 URL
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

const GalleryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { galleries } = useGalleries();

  const gallery = galleries.find(g => g.id === Number(id));

  // 페이지 진입 시 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!gallery) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500">갤러리를 찾을 수 없습니다.</p>
              <button
                onClick={() => navigate('/gallery')}
                className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                목록으로
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* 제목·설명·메타 */}
            <div className="px-6 sm:px-8 py-6 sm:py-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                {gallery.title}
              </h1>

              {/* 메타 정보 */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm sm:text-base text-gray-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(gallery.created_at).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              {gallery.description && (
                <p className="text-base sm:text-lg text-gray-700 whitespace-pre-wrap">
                  {gallery.description}
                </p>
              )}
            </div>

            {/* 이미지 */}
            {gallery.images && gallery.images.length > 0 ? (
              <div className="w-full px-6 sm:px-8 pb-6 sm:pb-8 space-y-4">
                {gallery.images.map((url, index) => (
                  <div key={index} className="w-full bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={url}
                      alt={`${gallery.title} ${index + 1}`}
                      className="w-full h-auto object-contain mx-auto"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full px-6 sm:px-8 pb-6 sm:pb-8">
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={gallery.image_url || PLACEHOLDER_IMAGE}
                    alt={gallery.title}
                    className="w-full h-auto max-h-[70vh] object-contain mx-auto"
                  />
                </div>
              </div>
            )}

            {/* Footer 버튼 */}
            <div className="border-t border-gray-200 px-6 sm:px-8 py-4 sm:py-6 flex justify-end">
              <button
                onClick={() => navigate('/gallery')}
                className="px-4 sm:px-6 py-2 bg-gray-600 text-white text-sm sm:text-base rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GalleryDetail;
